import React from 'react';
import { useEffectOnce } from 'react-use';
import { always } from 'ramda';

import { Article } from '@definitions/articles.types';
import { Block, Header } from '@definitions/common.types';
import { ArticleHeader } from '@components/articles/articleHeader';
import { ContentBody } from '@components/contentBody';
import { renderIfExistsIn, renderWhenTrue } from '@utils/rendering';
import { useMonogram } from '@hooks/useMonogram';
import { ProductsWithBasicTitle } from '@components/blocks/productsBlockWithBasicTitle';
import { RecommendedArticles } from '@components/blocks/recommendedArticles';

import { Container } from './articleView.styles';

export interface ArticleViewProps {
  article: Article;
}

export const ArticleView = ({ article }: ArticleViewProps) => {
  const { recommendedArticles = [], monogram, body, backgroundColor, textColor } = article;
  const renderArticleHeader = renderIfExistsIn(['header', 0], (header: Header) => <ArticleHeader header={header} />);
  const { addMonogram, clearMonogram } = useMonogram();

  useEffectOnce(() => {
    const id = new Date().getTime();

    if (monogram?.monogramLetter?.length) {
      const { monogramLetter, monogramColor, monogramAnimationType } = monogram;

      addMonogram({
        letter: monogramLetter,
        color: monogramColor,
        appearance: monogramAnimationType,
        id,
      });
    }

    return () => {
      clearMonogram(id);
    };
  });

  const renderFeaturedArtworks = renderIfExistsIn(['featuredProducts', 0], (featuredProductsBlock: Block) => (
    <ProductsWithBasicTitle {...featuredProductsBlock.value} />
  ));
  const renderRecommendedArticles = renderWhenTrue(always(<RecommendedArticles articles={recommendedArticles} />));

  return (
    <Container>
      {renderArticleHeader(article)}
      <ContentBody body={body} hasSpacingTop={false} backgroundColor={backgroundColor} textColor={textColor} />
      {renderFeaturedArtworks(article)}
      {article.readOn && renderRecommendedArticles(!!recommendedArticles.length)}
    </Container>
  );
};
